import { useState, useContext } from "react";
import { Menu, X, Phone, Mail } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { ScrollContext } from "../../context/scrollContext";
import { Link, useLocation } from "react-router-dom";

function Header() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const scrolled = useContext(ScrollContext);
  const location = useLocation();
  const isHome = location.pathname === "/";
  const showDarkBackground = isHome ? scrolled : true;

  const closeMenu = () => setIsMenuOpen(false);

  const navItems = [
    { label: "Home", href: "/" },
    { label: "Featured Work", href: "/featured-work" },
    { label: "Our Story", href: "/our-story" },
    { label: "Contact Us", href: "/contact" },
  ];

  const itemVariants = {
    hidden: { opacity: 0, x: -30 },
    visible: (i) => ({
      opacity: 1,
      x: 0,
      transition: { delay: 0.4 + i * 0.08, duration: 0.3 },
    }),
  };

  return (
    <>
      <header
        className={`fixed top-0 left-0 w-full z-50 transition-all duration-300 p-4 ${
          showDarkBackground
            ? "bg-transparent backdrop-blur-md shadow-md text-black"
            : "bg-transparent text-white"
        }`}
      >
        <div
          className={`p-2 ${
            showDarkBackground ? "bg-white rounded-2xl" : "bg-transparent"
          }`}
        >
          <div className="max-w-7xl mx-auto flex items-center justify-between px-6 py-3 w-full">
            {/* Desktop View: Logo + Nav */}
            <div className="hidden md:flex items-center justify-between w-full relative">
              {/* Left: Logo (conditionally hidden) */}
              {showDarkBackground && (
                <Link
                  to="/"
                  className="text-3xl font-semibold tracking-wide whitespace-nowrap"
                >
                  <h1
                    className="leading-none"
                    style={{ fontFamily: "Montserrat" }}
                  >
                    <span className="font-semibold">ARK</span>
                    <span className="font-normal">BUILD</span>
                  </h1>
                </Link>
              )}

              {/* Center: Navigation - always centered */}
              <div className="absolute left-1/2 -translate-x-1/2">
                <nav className="flex gap-10">
                  {navItems.map((item) => (
                    <Link
                      key={item.label}
                      to={item.href}
                      className="text-base font-medium tracking-wide hover:opacity-80 transition-opacity duration-200"
                    >
                      {item.label}
                    </Link>
                  ))}
                </nav>
              </div>
            </div>

            {/* Mobile View: Logo + Hamburger */}
            <div className="flex justify-between items-center w-full md:hidden">
              <Link to="/" className="text-2xl font-semibold tracking-wide">
                <h1
                  className="leading-none"
                  style={{ fontFamily: "Montserrat" }}
                >
                  <span className="font-semibold">ARK</span>
                  <span className="font-normal">BUILD</span>
                </h1>
              </Link>

              <button
                onClick={() => setIsMenuOpen(true)}
                className="flex items-center gap-2 text-base py-2 px-4 font-medium tracking-wider"
              >
                <Menu size={18} />
                <span>Menu</span>
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Slide-out Mobile Menu */}
      <AnimatePresence>
        {isMenuOpen && (
          <>
            <motion.div
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              transition={{ duration: 0.6, ease: "easeInOut" }}
              className="fixed top-0 right-0 h-full w-[80%] max-w-sm z-[999] text-white bg-black/70 backdrop-blur-md"
            >
              <div className="flex justify-end px-6 py-4">
                <button
                  onClick={closeMenu}
                  className="flex items-center gap-2 text-sm uppercase tracking-wider"
                >
                  <span>Close</span>
                  <X size={18} />
                </button>
              </div>

              <nav className="px-12 pt-6 flex flex-col items-start gap-6 text-2xl font-light leading-relaxed">
                {navItems.map((item, i) => (
                  <motion.a
                    key={item.label}
                    href={item.href}
                    onClick={closeMenu}
                    custom={i}
                    initial="hidden"
                    animate="visible"
                    exit="hidden"
                    variants={{
                      hidden: { opacity: 0, x: 30 },
                      visible: (i) => ({
                        opacity: 1,
                        x: 0,
                        transition: { delay: 0.4 + i * 0.08, duration: 0.3 },
                      }),
                    }}
                    className="block"
                  >
                    {item.label}
                  </motion.a>
                ))}
              </nav>
            </motion.div>

            <motion.div
              className="fixed inset-0 bg-black/30 z-[998] backdrop-blur-sm"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={closeMenu}
            />
          </>
        )}
      </AnimatePresence>
    </>
  );
}

export default Header;
